import React, { useState, useEffect, useRef } from 'react';
import { secureGet, securePost, securePut } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';

import plusIcon from '../../icons/plusD.svg';
import minusIcon from '../../icons/minusD.svg';

const TaskForm = ({
    title,
    projectId,
    initialValues = {},
    onSuccess,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [formValues, setFormValues] = useState({
        assignedToId: '',
        description: '',
        estimatedDuration: 0,
        productId: '',
        startDate: '',
        endDate: '',
    });
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [technicians, setTechnicians] = useState([]);
    const [durationInputValue, setDurationInputValue] = useState('00:00'); // Local state for input display
    const durationInputRef = useRef(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    // Fetch technicians on component mount
    useEffect(() => {
        const fetchTechnicians = async () => {
            try {
                const response = await secureGet('/users/ROLE_TECHNICIAN', showSnackbar);
                console.log("technicians fetched data:", response.data);
                setTechnicians(response.data || []);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Failed to fetch technicians:", error);
                }
            }
        };
        fetchTechnicians();
    }, []);

    // Fetch services on component mount
    useEffect(() => {
        const fetchServices = async () => {
            setLoadingServices(true);
            try {
                const abortController = new AbortController();
                const response = await secureGet("/products/unpaginated?familyTypes=SERVICE", showSnackbar, { 
                    signal: abortController.signal 
                });
                const servicesData = response.data || [];
                setServices(servicesData.map((service) => ({
                    value: service.id,
                    label: service.name || service.reference || '',
                    name: service.name || service.reference || '',
                    reference: service.reference,
                })));
            } catch (error) {
                console.error("Failed to fetch services:", error);
            } finally {
                setLoadingServices(false);
            }
        };
        fetchServices();
    }, []);

    // Convert minutes to HH:MM format
    const minutesToHHMM = (minutes) => {
        if (!minutes || minutes === 0) return '00:00';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    };

    // Parse HH:MM format to minutes
    const parseHHMMToMinutes = (hhmm) => {
        if (!hhmm || hhmm === '') return 0;
        // Remove any non-digit characters except colon
        const cleaned = hhmm.replace(/[^\d:]/g, '');
        const parts = cleaned.split(':');
        if (parts.length === 2) {
            const hours = parseInt(parts[0], 10) || 0;
            const mins = parseInt(parts[1], 10) || 0;
            // Ensure minutes are between 0-59
            const validMins = Math.min(59, Math.max(0, mins));
            return hours * 60 + validMins;
        }
        // If no colon, try to parse as minutes directly
        const parsed = parseInt(cleaned, 10);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Format input value while typing (allows partial input)
    const formatInputValue = (value) => {
        if (!value || value === '') return '';
        // Remove any non-digit characters
        const digits = value.replace(/\D/g, '');
        
        if (digits.length === 0) return '';
        if (digits.length <= 2) {
            // First 1-2 digits are hours
            return digits;
        }
        // When 3+ digits, format as HH:MM
        // Last 2 digits are minutes, everything before is hours
        const hours = digits.slice(0, -2);
        const mins = digits.slice(-2);
        // Limit hours to 2 digits max
        const hoursPart = hours.length > 2 ? hours.slice(-2) : hours;
        return `${hoursPart}:${mins}`;
    };

    useEffect(() => {
        if (initialValues && initialValues.id) {
            // Modify mode - populate form with initial values
            // Backend might send hours (decimal) or minutes (integer)
            // If it's less than 24, assume it's hours; otherwise assume minutes
            let durationInMinutes = 0;
            const duration = initialValues.estimatedDuration || 0;
            if (duration < 24 && duration % 1 !== 0) {
                // Likely hours (decimal, e.g., 1.5)
                durationInMinutes = Math.round(duration * 60);
            } else {
                // Likely minutes (integer)
                durationInMinutes = Math.round(duration);
            }
            
            setFormValues({
                assignedToId: initialValues.assignedToId || '',
                description: initialValues.description || '',
                estimatedDuration: durationInMinutes, // Duration stored in minutes
                productId: initialValues.productId || '',
                // Keep startDate and endDate from initialValues for payload consistency
                startDate: initialValues.startDate || '',
                endDate: initialValues.endDate || '',
            });
            // Update input display value
            setDurationInputValue(minutesToHHMM(durationInMinutes));
        } else {
            // Add mode - reset form (default to 0 minutes)
            setFormValues({
                assignedToId: '',
                description: '',
                estimatedDuration: 0,
                productId: '',
                startDate: '',
                endDate: '',
            });
            // Reset input display value
            setDurationInputValue('00:00');
        }
    }, [initialValues]);

    const handleInputChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleDurationChange = (delta) => {
        setFormValues((prev) => {
            // Increment/decrement by 10 minutes
            const increment = 10;
            const newDuration = prev.estimatedDuration + (delta * increment);
            const finalDuration = Math.max(0, newDuration);
            // Update input display value
            setDurationInputValue(minutesToHHMM(finalDuration));
            return {
                ...prev,
                estimatedDuration: finalDuration,
            };
        });
    };

    const handleDurationInputChange = (value) => {
        // If current value is "00:00" and user starts typing, clear it first
        if (durationInputValue === '00:00' && value && value !== '00:00') {
            // Remove the "00:00" and start fresh with what user typed
            const digits = value.replace(/\D/g, '');
            if (digits.length > 0) {
                const formatted = formatInputValue(digits);
                setDurationInputValue(formatted);
                return;
            }
        }
        // Allow user to type freely, format as they type
        const formatted = formatInputValue(value);
        setDurationInputValue(formatted);
    };

    const handleDurationInputFocus = (e) => {
        // If value is "00:00", clear it so user can type immediately
        if (durationInputValue === '00:00') {
            setDurationInputValue('');
        } else {
            // Otherwise, select all text when focused
            e.target.select();
        }
    };

    const handleDurationInputBlur = () => {
        // On blur, parse and validate the input, then update the actual duration
        // If input is empty or "00:00", set to 0
        const inputValue = durationInputValue === '' ? '00:00' : durationInputValue;
        const minutes = parseHHMMToMinutes(inputValue);
        setFormValues((prev) => ({
            ...prev,
            estimatedDuration: minutes,
        }));
        // Format the display value properly (show "00:00" as placeholder on next focus)
        setDurationInputValue(minutes === 0 ? '00:00' : minutesToHHMM(minutes));
    };

    const formatDuration = (minutes) => {
        // Format minutes to display as "Xh" or "XhYm" format
        if (!minutes || minutes === 0) return '0h';
        
        const wholeHours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        
        if (mins === 0) {
            return `${wholeHours}h`;
        } else if (wholeHours === 0) {
            return `${mins}min`;
        } else {
            return `${wholeHours}h${mins}min`;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();



        setLoading(true);

        try {
            const payload = {
                assignedToId: parseInt(formValues.assignedToId, 10),
                description: formValues.description,
                estimatedDuration: parseInt(formValues.estimatedDuration, 10), // Send as minutes (integer)
            };

            // Include productId if selected
            if (formValues.productId) {
                payload.productId = parseInt(formValues.productId, 10);
            }

            // Include startDate and endDate if they exist (for modify mode to keep initialValues structure)
            if (formValues.startDate) {
                payload.startDate = formValues.startDate;
            }
            if (formValues.endDate) {
                payload.endDate = formValues.endDate;
            }

            if (initialValues?.id) {
                // Modify mode - PUT request
                payload.id = parseInt(initialValues.id, 10);
                await securePut(`/projects/tasks/${initialValues.id}`, payload, showSnackbar);
                onSuccess(t('confirmation.taskUpdated') || 'Tâche modifiée avec succès');
            } else {
                // Add mode - POST request
                console.log('Submitting payload:', payload);
                await securePost(`/projects/${projectId}/tasks/add`, payload, showSnackbar);
                onSuccess(t('confirmation.taskAdded') || 'Tâche ajoutée avec succès');
            }
        } catch (error) {
            console.error("Failed to submit task:", error);
        } finally {
            setLoading(false);
        }
    };

    const technicianOptions = technicians.map((tech) => ({
        value: tech.id,
        label: `${tech.firstName || ''} ${tech.lastName || ''}`.trim() || tech.username || tech.name,
        name: `${tech.firstName || ''} ${tech.lastName || ''}`.trim() || tech.username || tech.name,
    }));

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
            <div className="bg-[#F9FAFC] rounded-[12px] shadow-lg px-6 py-2 w-[90%] max-w-[700px] max-h-[90vh] flex flex-col">
                <Helmet>
                    <title>Drivnest - {title}</title>
                </Helmet>

                {/* Header */}
                <div className="p-6 pb-0">
                    <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
                </div>

                {/* Content */}
                <div className="px-6 py-4 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex justify-center items-center h-[300px]">
                            <LoadingLogo />
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Services */}
                            <div>
                                <Input
                                    type="dropdown"
                                    label='Services'
                                    name="productId"
                                    value={formValues.productId}
                                    onChange={(value) => handleInputChange('productId', value)}
                                    options={services}
                                    disabled={loadingServices}
                                />
                            </div>

                            {/* Technician Assignment and Duration on same row */}
                            <div className="grid grid-cols-2 gap-4">
                                {/* Technician Assignment - Half width */}
                                <div>
                                    <Input
                                        type="dropdown"
                                        label={t('fields.assignedTo')}
                                        name="assignedToId"
                                        value={formValues.assignedToId}
                                        onChange={(value) => handleInputChange('assignedToId', value)}
                                        options={technicianOptions}
                                    />
                                </div>

                                {/* Duration - Half width */}
                                <div className="flex items-center gap-3">
                                    <label className="text-[12px] font-semibold text-[#707070] uppercase whitespace-nowrap">
                                        {'DURÉE'}
                                    </label>
                                    <div className="flex items-stretch rounded-[12px] border border-[#E3E3E3] bg-[#ffffff] divide-x divide-[#E3E3E3]">
                                        <button
                                            type="button"
                                            onClick={() => handleDurationChange(-1)}
                                            className="w-10 h-10 flex items-center justify-center bg-white rounded-l-[12px] hover:bg-gray-100 flex-shrink-0"
                                        >
                                            <img src={minusIcon} alt="Minus" className="w-4 h-4" />
                                        </button>
                                        <input
                                            ref={durationInputRef}
                                            type="text"
                                            value={durationInputValue === '00:00' ? '' : durationInputValue}
                                            onChange={(e) => handleDurationInputChange(e.target.value)}
                                            onFocus={handleDurationInputFocus}
                                            onBlur={handleDurationInputBlur}
                                            placeholder="00:00"
                                            className="bg-white px-4 text-center text-[16px] font-bold text-[#38485C] focus:outline-none border-0 w-[160px] placeholder:text-gray-400"
                                            pattern="[0-9]{1,2}:[0-9]{2}"
                                            maxLength={5}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleDurationChange(1)}
                                            className="w-10 h-10 flex items-center justify-center bg-white rounded-r-[12px] hover:bg-gray-100 flex-shrink-0"
                                        >
                                            <img src={plusIcon} alt="Plus" className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="text-[12px] font-semibold text-[#707070] mb-2 block">
                                    {t('fields.description')}
                                </label>
                                <textarea
                                    name="description"
                                    value={formValues.description}
                                    optional={true}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    className="w-full min-h-[200px] px-4 py-3 border border-gray-300 rounded-[12px] text-[16px] font-semibold text-[#38485C] focus:outline-none focus:border-primary resize-y"
                                    placeholder={t('fields.description')}
                                />
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer with Save/Cancel */}
                <div className="p-6 pt-0 flex justify-end">
                    <SaveCancel
                        onCancelClick={onCancel}
                        onSaveClick={handleSubmit}
                        loading={loading}
                    />
                </div>
            </div>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />
        </div>
    );
};

export default TaskForm;

